function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

const PropertyInitArgs = require('../entities/property');

function _call(body, then, direct) {
  if (direct) {
    return then ? then(body()) : body();
  }

  try {
    var result = Promise.resolve(body());
    return then ? result.then(then) : result;
  } catch (e) {
    return Promise.reject(e);
  }
}

const _ = require('lodash');

const diff = require('../utils/diff');

const AccessContext = require('./contexts/access-context');

const PagedList = require('../entities/paged-list');

const Property = require('../entities/property');

const Plan = require('../entities/plan');

const IntegrationsRepo = require('./integrations-repo');

const DestinationsRepo = require('./destinations-repo');

const UsersRepo = require('./users-repo');

const TokensRepo = require('./tokens-repo');

const BusinessesRepo = require('./businesses-repo');

const LocationsRepo = require('./locations-repo');

const OffersRepo = require('./offers-repo');

const ClaimRepo = require('./claim-repo');

const PlansRepo = require('./plans-repo');

const HmacsRepo = require('./hmacs-repo');

const ImageLinksRepo = require('./image-links-repo');

const CollectionsRepo = require('./collections-repo');

const StatsRepo = require('./stats-repo');

const {
  strList
} = require('../utils/serialize');

function parse(json
/*: Object*/
)
/*: Property*/
{
  const {
    id,
    name,
    status,
    subdomain,
    images = []
  } = json;
  return new Property({
    id,
    subdomain,
    status,
    name,
    logoImage: images.find(imageId => imageId.split('_')[0] === 'property-logo-standard'),
    profileImage: images.find(imageId => imageId.split('_')[0] === 'property-profile'),
    splashImage: images.find(imageId => imageId.split('_')[0] === 'property-splash-standard'),
    logoStandardImageUrl: _.get(json, 'logoStandardImages[0].url'),
    profileImageUrl: _.get(json, 'profileImages[0].url'),
    splashStandardImageUrl: _.get(json, 'splashStandardImages[0].url')
  });
}

function serialize(data)
/*: Object*/
{
  return {
    id: data.id,
    name: data.name,
    status: data.status,
    subdomain: data.subdomain,
    images: _.compact([data.logoImage, data.profileImage, data.splashImage])
  };
}

const fields = ['id', 'subdomain', 'status', 'name', 'images'];
const mineFields = ['id', 'name', 'subdomain', 'status', 'profileImages.url'];

class PropertyRepo {
  constructor(client
  /*: ApiClient*/
  , id
  /*: propertyId*/
  ) {
    this.id = id;
    this.context = new AccessContext(id);
    this.client = client;
    this.integrations = new IntegrationsRepo(client, this.context);
    this.destinations = new DestinationsRepo(client, this.context);
    this.users = new UsersRepo(client, this.context);
    this.tokens = new TokensRepo(client, this.context);
    this.businesses = new BusinessesRepo(client, this.context);
    this.locations = new LocationsRepo(client, this.context);
    this.offers = new OffersRepo(client, this.context);
    this.plans = new PlansRepo(client, this.context);
    this.hmacs = new HmacsRepo(client, this.context);
    this.collections = new CollectionsRepo(client, this.context);
    this.images = new ImageLinksRepo(client, this.context);
    this.stats = new StatsRepo(client, this.context);
  }

  details()
  /*: Promise<Property>*/
  {
    const req = new this.client.Request(`/properties/${this.id}`, {}, fields);
    req.parse = parse;
    return this.client.get(req);
  }

  getBusinessPlanSummary() {
    const path = `/self/properties/${this.id}/summary/search`;
    const req = new this.client.Request(path, {}, ['plans.id', 'plans.name', 'plans.count', 'businessCount', 'locationCount']);
    return this.client.get(req);
  }

  detailsWithLocales()
  /*: Promise<Property>*/
  {
    const _this = this;

    return _call(function () {
      return _await(Promise.all([_this.details(), _this.getLocales().then(res => res.locales)]), function ([property, locales]) {
        property.locales = locales;
        return property;
      });
    });
  }

  getLocales()
  /*: Promise<[string]>*/
  {
    const req = new this.client.Request(`/properties/${this.id}/locales`, {}, ['locales']);
    return this.client.get(req);
  }

  userAccesses(userId
  /*: string*/
  ) {
    const url = `/properties/${this.id}/users/${userId}/accesses/search`;
    const req = new this.client.Request(url, {}, ['id', 'propertyId', 'businessId', 'locationId', 'roleId']);
    return this.client.get(req);
  }

  update(inst
  /*: string | Property*/
  , updates
  /*: Property | Object*/
  ) {
    const {
      propertyId
    } = this.context.serialize();
    let updatedProps = updates;

    if (inst instanceof Property) {
      updatedProps = diff(inst, updates);
      updatedProps.profileImage = updates.profileImage || inst.profileImage;
      updatedProps.logoImage = updates.logoImage || inst.logoImage;
      updatedProps.splashImage = updates.splashImage || inst.splashImage;
    } // Don't try to PATCH these...


    updatedProps = _.omit(updatedProps, ['id']);
    const req = new this.client.Request(`/properties/${propertyId}`, updatedProps, fields);
    req.parse = parse;
    req.serialize = serialize;
    return this.client.patch(req);
  }

  getSettings() {
    const settingsFields = ['key', 'value'];
    const req = new this.client.Request(`/properties/${this.id}/settings`, {}, settingsFields);
    return this.client.get(req);
  }

  getSetting(key
  /*: string*/
  ) {
    const settingFields = ['value'];
    const req = new this.client.Request(`/properties/${this.id}/settings/${key}`, {}, settingFields);
    return this.client.get(req);
  }

  setSetting(key
  /*: string*/
  , value
  /*: any*/
  ) {
    const params = {
      value
    };
    const req = new this.client.Request(`/properties/${this.id}/settings/${key}`, params);
    return this.client.put(req);
  }

  delete()
  /*: Promise<boolean>*/
  {
    return this.client.delete(new this.client.Request(`/properties/${this.id}`));
  }

}

const DEFAULT_VERIFICATION_KEY_NAME = 'Verification Key';

class PropertiesRepo {
  constructor(client
  /*: ApiClient*/
  ) {
    this.client = client;

    this.for = (id
    /*: propertyId*/
    ) => new PropertyRepo(client, id);

    this.icanhas = this.for;
  } // TODO: Stop calling `list` where we want to list "mine"
  // Then make this match `listRaw`


  list()
  /*: (Promise<PagedList<Property>>)*/
  {
    const _this2 = this;

    return _call(function () {
      return _await(_this2.listMine());
    });
  }

  listMine()
  /*: Promise<PagedList<Property>>*/
  {
    const _this3 = this;

    return _call(function () {
      const path = `/self/properties/search`;
      const req = new _this3.client.Request(path, {}, mineFields);
      req.parse = parse;
      return _await(_this3.client.get(req));
    });
  }

  listMyProperties()
  /*: Promise<PagedList<Property>>*/
  {
    const _this4 = this;

    return _call(function () {
      const path = `/self/properties/search`;
      const req = new _this4.client.Request(path, {
        context: strList(['P'])
      }, mineFields);
      req.parse = parse;
      return _await(_this4.client.get(req));
    });
  }

  listRaw()
  /*: (Promise<PagedList<Property>>)*/
  {
    const req = new this.client.Request(`/properties`, {}, fields);
    req.parse = parse;
    return this.client.get(req);
  }

  bySubdomain(subdomain
  /*: string*/
  ) {
    const publicFields = ['id', 'logoStandardImages.url', 'profileImages.url', 'name', 'splashStandardImages.url', 'settings.selfSignUpEnabled', 'settings.self_signup.enabled', 'settings.business_dashboard.enabled', 'settings.locales', 'settings.hosted_redemption.enabled', 'settings.hosted_redemption.integration_key', 'settings.hosted_redemption.theme', 'settings.homepage.url', 'settings.mobile.android.install_url', 'settings.mobile.ios.install_url'];
    const req = new this.client.Request(`/subdomains/${subdomain}/property`, {}, publicFields);
    req.parse = parse;
    return this.client.get(req).then(properties => {
      // HACK: Workaround until we replace everything with the useProperty hook
      properties.items.forEach(property => {
        /* eslint-disable-next-line no-param-reassign */
        property.subdomain = subdomain;
      });
      return properties;
    });
  }
  /**
   * Create a new property
   *
   * Process is as follows:
   *
   * - Create new property
   * - Claim it (to set yourself as the "owner").
   *
   * This gives the current user full control over the new property.
   */


  create(args
  /*: PropertyInitArgs*/
  )
  /*: Promise<Property>*/
  {
    const _this5 = this;

    return _call(function () {
      // TODO: There is absolutely no error handling below. There are still
      // too many requests, but it's a lot better now.
      return _await(_this5.client.auth.profile(), function (user) {
        const prop = new Property(args);
        const req = new _this5.client.Request(`/properties`, prop, fields);
        req.parse = parse;
        req.serialize = serialize;
        return _await(_this5.client.post(req), function (newProperty) {
          const context = new AccessContext(newProperty);
          const claim = new ClaimRepo(_this5.client);
          return _await(claim.claim(context, user), function () {
            return _await(_this5.for(newProperty.id).hmacs.create({
              name: DEFAULT_VERIFICATION_KEY_NAME
            }), function () {
              return _await(_this5.for(newProperty.id).plans.create(new Plan({
                name: 'Default Plan',
                description: 'The default plan for businesses.',
                discoverable: true,
                enabled: true,
                settings: []
              })), function () {
                return newProperty;
              });
            });
          });
        });
      });
    });
  }

}

module.exports = PropertiesRepo;